var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { usaStateFromName } from "healthex-common/usaStates";
import { UserType } from "healthex-common/user.dtos";
import { computed, observable } from "mobx";
import { NOT_LOADED } from "../Infra/RemoteData";
import { validators } from "../Logic/Validators";
import { FormModel } from "./FormModel";
export class AdminOrganizationFormModel {
    constructor(opts) {
        this.loading = false;
        this.logoUrlWasSet = false;
        this.formModel = new FormModel({
            name: "",
            logo: null,
            address: "",
            city: "",
            state: "",
            zip: "",
            noPatientPortal: false,
            patientPortalName: "",
            patientPortalLink: "",
            vanitySubdomain: "",
        }, ["name", "address", "city", "state", "zip", "patientPortalName", "logo"]);
        this.organization = NOT_LOADED;
        this.organizationsManager = opts.container.organizationsManager;
        this.adminUIManager = opts.container.adminUIManager;
        this.environment = opts.container.environment;
        this.onSave = opts.onSave;
        void this.loadOrganization();
    }
    async loadOrganization(reload) {
        this.loading = true;
        try {
            if (reload) {
                await this.organizationsManager.myOrganization.waitForReload();
            }
            else {
                await this.organizationsManager.myOrganization.waitForValue();
            }
            const organization = this.organizationsManager.myOrganization.value;
            if (organization === NOT_LOADED)
                return;
            if (!organization)
                return;
            this.organization = organization;
            const { name, logoUrl, vanitySubdomain, address, city, state, zip } = organization;
            this.logoUrlWasSet = !!logoUrl;
            this.formModel.input = {
                ...this.formModel.input,
                name,
                logo: logoUrl,
                vanitySubdomain: vanitySubdomain !== null && vanitySubdomain !== void 0 ? vanitySubdomain : "",
                address: address !== null && address !== void 0 ? address : "",
                city: city !== null && city !== void 0 ? city : "",
                state: state !== null && state !== void 0 ? state : "",
                zip: zip !== null && zip !== void 0 ? zip : "",
            };
            if (organization.type === UserType.HEALTHCARE_ORGANIZATION) {
                const { patientPortalName, patientPortalLink } = organization;
                this.formModel.input = {
                    ...this.formModel.input,
                    noPatientPortal: patientPortalName === null,
                    patientPortalName: patientPortalName !== null && patientPortalName !== void 0 ? patientPortalName : "",
                    patientPortalLink: patientPortalLink !== null && patientPortalLink !== void 0 ? patientPortalLink : "",
                };
            }
        }
        finally {
            this.loading = false;
        }
    }
    isOrganizationUserType(...types) {
        var _a;
        if (this.organization === NOT_LOADED || this.organization === null)
            return false;
        return types.includes((_a = this.organization) === null || _a === void 0 ? void 0 : _a.type);
    }
    get showPatientPortalFields() {
        return this.isOrganizationUserType(UserType.HEALTHCARE_ORGANIZATION);
    }
    validateInput() {
        this.formModel.clearErrors();
        const { notBlank, notNull, validZIP, validURL } = validators;
        const f = this.formModel;
        f.validateField("name", notBlank, "Enter your organization's name");
        f.validateField("address", notBlank, "Enter an address");
        f.validateField("city", notBlank, "Enter a city");
        f.validateField("state", notNull, "Select a state from the list");
        f.validateField("zip", notBlank, "Enter a valid 5-digit ZIP code");
        f.validateField("zip", validZIP, "Enter a valid 5-digit ZIP code");
        if (this.showPatientPortalFields) {
            if (!f.input.noPatientPortal) {
                f.validateField("patientPortalName", notBlank, "Enter your patient portal's name or check the box above");
            }
            f.validateField("patientPortalLink", validURL, "Enter a valid URL");
        }
        f.validateField("logo", notNull, "Upload a logo for your organization");
        f.validateField("vanitySubdomain", (subdomain) => subdomain.length === 0 || !!subdomain.match(/^[a-zA-Z0-9][[a-zA-Z0-9]{0,62}$/), "Your custom URL must contain 1-63 letters, numbers, or dashes. Do not use spaces, symbols, or special characters.");
        return !f.hasErrors();
    }
    async save() {
        const success = await this.performUpdate();
        if (success) {
            await this.loadOrganization(true);
            this.onSave();
        }
    }
    async performUpdate() {
        if (this.organization === NOT_LOADED || this.organization === null)
            return false;
        if (!this.validateInput())
            return false;
        const { name, logo, address, city, state, zip, patientPortalName, patientPortalLink, vanitySubdomain } = this.formModel.input;
        const updatedOrganization = {
            type: this.organization.type,
            name,
            logo,
            address,
            city,
            state: usaStateFromName(state),
            zip,
            patientPortalName: patientPortalName.length ? patientPortalName : null,
            ssoEnabled: false,
            patientPortalLink: patientPortalLink.length ? patientPortalLink : null,
            vanitySubdomain: vanitySubdomain.length ? vanitySubdomain : null,
        };
        await this.organizationsManager.editOrganization(this.organization.id, updatedOrganization);
        if (!this.logoUrlWasSet && logo !== null) {
            this.adminUIManager.openGoLiveChecklistDialogModel();
            this.logoUrlWasSet = true;
        }
        return true;
    }
    get vanitySubdomainSuffix() {
        return `.${this.environment.WEB_ROOT_DOMAIN}`;
    }
}
__decorate([
    observable,
    __metadata("design:type", Object)
], AdminOrganizationFormModel.prototype, "loading", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], AdminOrganizationFormModel.prototype, "organization", void 0);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AdminOrganizationFormModel.prototype, "showPatientPortalFields", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AdminOrganizationFormModel.prototype, "vanitySubdomainSuffix", null);
